import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { InventoryItem, SellerOfferDto, DateUtilsService } from 'remarketing-angular-library';
import { forEach } from 'lodash';
import { Select } from '@ngxs/store';
import { OffersQueries } from '../../queries/offers.queries';
import { map, takeUntil, filter} from 'rxjs/operators';
import { DateTimeFormat } from 'remarketing-angular-library/enums/dateTime.enum';
import { CurrencyFormatService, OfferStatusPipe } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import { TooltipComponent } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';

export interface PreviousOffers {
  dates: string[]; // ['2019-01-05', '2019-01-02', ...]
  offersByDate: { [key: string]: SellerOfferDto[] }; // { '2019-01-05': [Offer, Offer], ...}
}

export interface OfferHistory {
  offer: SellerOfferDto;
  previousOffers: PreviousOffers;
}

@Component({
    selector: 'lib-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.scss'],
    imports: [TranslocoDirective, OfferStatusPipe, TooltipComponent, CommonModule]
})
export class HistoryComponent implements OnInit, OnDestroy {
  private offer$$ = new BehaviorSubject<SellerOfferDto>(undefined);
  private _isVisible = false;
  @Output() closed = new EventEmitter<boolean>();
  @Input() set isVisible(isVisible: boolean) {
    this._isVisible = isVisible;
    if (this._isVisible) {
      setTimeout(() => this.heading.nativeElement.focus());
    }
  }
  get isVisible() {
    return this._isVisible;
  }
  @ViewChild('heading') heading: ElementRef<HTMLHeadingElement>;
  @Input() set offer(offer: SellerOfferDto) {
    this.offer$$.next(offer);
  }
  @Input() index?: number;
  @Input() inventoryItem: Partial<InventoryItem>;
  @Input() tenantName: string;
  @Input() showBuyerUserNameToSeller: boolean;
  destroyed$ = new Subject();
  historyCount = 0;

  @Select(OffersQueries.history) private offerHistory$: Observable<(id: string) => OfferHistory>;
  offerHistory: OfferHistory;

  isMobile: boolean;

  get mainOffer() {
    return this.offer$$.getValue();
  }

  get rootOffer() {
    return this.offerHistory ? this.offerHistory.offer : undefined;
  }

  get previousOffers() {
    return this.offerHistory ? this.offerHistory.previousOffers : { dates: [], offersByDate: {} };
  }

  get defaultOfferDate() {
    try {
      const latestPreviousOffers = this.offerHistory.previousOffers;
      return latestPreviousOffers.dates[latestPreviousOffers.dates.length - 1];
    } catch (e) {
      return '';
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.detectIfMobile();
  }

  constructor(
    private readonly dateUtilService: DateUtilsService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {}

  ngOnInit(): void {
    const offer$ = this.offer$$.pipe(filter(o => !!o));
    combineLatest(this.offerHistory$, offer$)
      .pipe(
        takeUntil(this.destroyed$),
        map(([filterFn, offer]) => filterFn(offer.offerUuid))
      )
      .subscribe(history => {
        this.offerHistory = history;
        let count = 0;
        if (this.offerHistory && this.offerHistory.previousOffers) {
          const dates = this.offerHistory.previousOffers.offersByDate;
          forEach(dates, key => {
            count += key.length;
          });
        }
        this.historyCount = count;
      });
    this.detectIfMobile();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  formatDate(date) {
    return this.dateUtilService.localizeDate(date);
  }

  offerDate(date) {
    return this.dateUtilService.localizeTime(date, true);
  }

  getOfferUserName(offer) {
    if (offer.userProfile && offer.userProfile.userName) {
      return offer.userProfile.userName;
    }
    return '';
  }

  detectIfMobile() {
    this.isMobile = window.matchMedia('(max-width: 645px)').matches;
  }

  closeClicked() {
    this.closed.emit();
  }

  formatCurrency(currency) {
    return this.currencyFormatService.formatCurrency(currency, false).replaceAll('-', '');
  }
}
